import React, { CSSProperties, useEffect } from "react";
import styled from "styled-components";
import Footer from "./Footer";
import { compare01, compare02 } from "../model/DataConst";
import { OrientationContext, OrientationEnum } from "../provider/OrientationProvider";
import BackToMain from "./BackToMain";
import { IUADevice, userAgentInfo } from "../model/UserAgentInfo";
import { MAX_WIDTH, MAX_WIDTH_STR } from "../model/Const";

const HandleSpan = styled.span`
    // background: #fff;
    bottom: 0;
    cursor: ew-resize;
    left: 50%;
    margin-left: -1px;
    position: absolute;
    top: 0;
    width: 0.5vmin;
	pointer-events: none;
	background: rgba(223, 0, 58, .5);
	
	&:before,
	&:after {
		content: '';
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
	}
	&:before {
		left: 1vmin;
		border: solid transparent;
		border-width: 2vmin;
		border-left-color: rgba(223, 0, 58, .7);
	}
	&:after {
		right: 1vmin;
		border: solid transparent;
		border-width: 2vmin;
		border-right-color: rgba(223, 0, 58, .7);
	}
`;

const CompareImg = styled.img`
	display: block;
	user-select: none;
	max-width: none !important;
`;

const DescriptionSection = styled.section`
	width: 90%;
	margin: 5%;
`;

interface ICompareImageSize {
	compareImgStyle: CSSProperties,
	compareParentStyle: CSSProperties
}

const calcCompareImageSize: () => ICompareImageSize = () => {
	// 幅はwindowの9割に固定
	const width = window.innerWidth > MAX_WIDTH ?
		Math.floor( MAX_WIDTH * 0.9 ) :
		Math.floor( window.innerWidth * 0.9 );
	return {
		compareImgStyle: {
			width: width + "px",
		},
		compareParentStyle: {
			height: width * 0.63 + "px", // 使う画像が分かっているので、縦横比で高さを計算
			position: "relative",
			width: "100%",
		}
	};
};

let prevOrientation: OrientationEnum;
const WhatIsMoire: React.FC = () => {

	// useRef
	const rangeRef = React.useRef<HTMLInputElement>( null );

	// useState
	const [onImgWidthPer, setOnImgWidthPer] = React.useState( "50%" );
	const [compareImgSize, setCompareImgSize] = React.useState( calcCompareImageSize() );

	// useContext
	const context = React.useContext( OrientationContext );

	// 最大幅を設定するスタイル（PCの場合のみ設定）
	const maxWidthStyle = React.useMemo( () => {
		// 幅が最大幅より大きく、PCだったら最大幅を指定
		if ( window.innerWidth > MAX_WIDTH && userAgentInfo.device === IUADevice.pc ) {
			return {
				max: {
					maxWidth: MAX_WIDTH_STR,
					margin: "auto",
					fontSize: "1.5rem",
				} as CSSProperties,
				title: {
					fontSize: "3rem",
				},
				strongMessage: {
					fontSize: "1rem",
				} as CSSProperties
			};
		}

		return {
			max: {
				// fontSize: "2vw",
			},
			title: {
				// fontSize: "4vw",
			},
			strongMessage: {
				fontSize: "0.8rem",
			}
		};
	}, [context.resize] );

	// Mobileあるいはタブレットで縦横が切り替わったらリロードする
	React.useEffect( () => {
		if ( prevOrientation === undefined ) {
			prevOrientation = context.orientation;
			return;
		}

		// PCは縦横変換しても無視
		if ( userAgentInfo.device === IUADevice.pc ) {
			return;
		}

		// PC以外の環境で縦横が変更されたらリロードする
		window.location.reload();
	}, [context.orientation] );

	// 一回だけ実行
	useEffect( () => {
		// 先頭にスクロールしておく
		window.scrollTo( 0, 0 );
	}, [] );

	// リサイズごとに実行
	useEffect( () => {
		setCompareImgSize( calcCompareImageSize() );
	}, [context.resize] );

	// refに要素が反映されたら実行（１回のみ実行される想定）
	useEffect( () => {
		if ( rangeRef.current === null ) {
			return;
		}

		const input = rangeRef.current;
		const func = () => {
			// inputのvalueの値で%（画像の表示割合）を計算
			setOnImgWidthPer( input.value + "%" );
		};

		// inputイベント設定
		input.addEventListener( "input", func );

		// inputイベント解除
		return function cleanup() {
			input.removeEventListener( "input", func );
		};

	}, [rangeRef.current] );

	return <>
		<main style={{
			overflow: "hidden",
			...maxWidthStyle.max,
		}}>
			<h1 style={{
				margin: "5%"
			}}>
				<p style={{
					fontFamily: "nicomoji",
					...maxWidthStyle.title,
				}}>モアレとは？</p>
			</h1>
			<DescriptionSection>
				<p> モアレは、規則正しく分布した線や点などが重なり合ったときに生じる、縞やマダラの模様です。まんが画像にはスクリーントーンと呼ばれる規則正しく並んだ網点が使用されるため、画像のサイズ変更や画面に表示する際に、モアレが生じることがあります。</p>
			</DescriptionSection>
			<section style={{
				margin: "5%"
			}}>
				<h2>
					<p style={{
						fontFamily: "nicomoji"
					}}>スライドしてモアレを見てみよう</p>
				</h2>
				<div style={compareImgSize.compareParentStyle}>
					<div style={{
						position: "absolute",
						width: "100%",
						height: "100%",
						left: "50%",
						transform: "translateX(-50%)",
					}}>
						<div style={{
							position: "relative",
							width: "100%",
							display: "inline-block",
						}}>
							<div style={{
								position: "absolute",
								top: "0",
								bottom: "0",
								left: "0",
								width: onImgWidthPer,
								overflow: "hidden",
							}}>
								<CompareImg
									src={compare01}
									style={compareImgSize.compareImgStyle}
									alt={"eComicScreen+未使用"}
								/>
							</div>
							<CompareImg
								src={compare02}
								style={compareImgSize.compareImgStyle}
								alt={"eComicScreen+使用"}
							/>
							<input
								ref={rangeRef}
								type="range"
								style={{
									position: "absolute",
									left: 0,
									top: 0,
									width: "100%",
									height: "100%",
									margin: "0",
									appearance: "none",
									cursor: "pointer",
									opacity: 0,
								}}/>
							<HandleSpan style={{
								left: onImgWidthPer
							}}/>
						</div>
					</div>
				</div>
				<div style={{
					width: "90%",
					margin: "0 5% 5% 5%",
					display: "flex",
					justifyContent: "space-between",
					...maxWidthStyle.strongMessage
				}}>
					<div>モアレあり</div>
					<div>モアレなし</div>
				</div>
				<p> これまで、電子コミック画像は天地サイズ1200
					ピクセル程度の小さなものが多く、印刷用のデータをデジタル用に縮小する際にスクリーントーンの網点がつぶれてモアレが軽減されていました。しかし、デジタルデバイスの高画質化を受けて電子コミックも高画質化が進み、天地1600
					ピクセル以上になるケースが増えています。そのため、従来の画像処理ではスクリーントーンの網点が残り、モアレが目立つようになっていました。</p>
			</section>
			<div style={{
				textAlign: "center",
				fontFamily: "nicomoji",
				fontSize: "1.4em"
			}}>
				<p><strong>これまでの制作方法では</strong><br/><strong>高解像度化するとモアレが発生するかも…</strong></p>
			</div>
		</main>

		{/*メインへ戻る*/}
		<BackToMain style={{
			opacity: 0.8
		}}/>

		{/*フッター*/}
		<Footer
			style={maxWidthStyle.max}/>
	</>;
};

export default WhatIsMoire;